import { CallStackResolution, callFramesEqualFn, CallFrameLike } from './CallStackResolution';
import { compareCausationNodes } from './CausationGraph';
import { CausationEdge, CausationNode } from './CausationNode';


export type CausationNodeAndRenderingData = ReturnType<typeof annotateCausationNodesForRendering>[number];
export const annotateCausationNodesForRendering = (nodes: CausationNode[], callStackResolution: CallStackResolution /* = 'frame'*/) => {
	const descendantsUnrenderedForNodeAtTabDepth: Set<CausationNode>[] = [];
	const callFramesEqual = callFramesEqualFn(callStackResolution);

	return nodes.map((node, index) => {
		const descendantsUnrenderedForNodeAtCurrentDepth = descendantsUnrenderedForNodeAtTabDepth[descendantsUnrenderedForNodeAtTabDepth.length - 1];
		const currentsDepthsIsParentOfThisNode = descendantsUnrenderedForNodeAtCurrentDepth?.has(node) ?? false;

		const tabDepthsOfNodesThatAreParentsOfThisNode = new Set<number>();
		descendantsUnrenderedForNodeAtTabDepth.forEach( (nodeSet, i) => {
			if (nodeSet.has(node)) {
				tabDepthsOfNodesThatAreParentsOfThisNode.add(i);
				nodeSet.delete(node);
			}
		});

		const parent = node.causedBy[0]?.node;
		const isOnlyDescendentOfSingleParent = currentsDepthsIsParentOfThisNode &&
			node.causedBy.length === 1 &&
			parent != null &&
			parent.willCause.length === 1;
		const increaseDepth = descendantsUnrenderedForNodeAtCurrentDepth == null ||
		  parent == null ||
			!isOnlyDescendentOfSingleParent ||
			!callFramesEqual(node as CallFrameLike, parent as CallFrameLike);

		if (increaseDepth) {
			descendantsUnrenderedForNodeAtTabDepth.push(new Set(node.willCause.map(({node}) => node)));
		} else {
			for (const willCause of node.willCause) {
				descendantsUnrenderedForNodeAtCurrentDepth.add(willCause.node);
			}
		}

		const tabDepthsOfNodesWithChildrenToBeRenderedBelowThisNode = new Set<number>(
			descendantsUnrenderedForNodeAtTabDepth
				.map((n, depth) => n.size > 0 ? depth : undefined)
				.filter(n => n != null)
		);

		const tabDepth = descendantsUnrenderedForNodeAtTabDepth.length;

		return {
			node,
			index: index + 1,
			firstAtDepth: increaseDepth,
			tabDepth,
			tabDepthsOfNodesWithChildrenToBeRenderedBelowThisNode,
			tabDepthsOfNodesThatAreParentsOfThisNode,
		} as const;
	});
};


export const filterGraphAndOrderNodesForRendering = (
	unorderedCausationGraphsNodes: CausationNode[],
	callStackResolution: CallStackResolution /* = 'frame'*/
) => {
	const areCallFramesEqual = callFramesEqualFn(callStackResolution);
	const alreadyRenderedNodeToItsReplacementInNewGraph = new Map<CausationNode, CausationNode>();
	const filteredOrderedNodes = [] as CausationNode[];
	const readyNodes = unorderedCausationGraphsNodes
		// Only the root nodes (those not caused by other nodes) are immediately ready to render
		.filter(n => n.causedBy.length === 0)
		// Sort the nodes by their timeStampIso property, if it exists
		.sort(compareCausationNodes);

	let node, previousNodeInFilteredGraph: CausationNode | undefined;
	while ((node = readyNodes.shift()) != null) {
		// Mark this node as rendered
		if ((node.causeType === 'call-frame' || node.causeType === 'initiator') &&
		previousNodeInFilteredGraph?.causeType === node.causeType &&
			areCallFramesEqual(node, previousNodeInFilteredGraph) &&
			node.willCause.length > 0
		) {
			// This node is the same as the previous node at the callStackResolution we are displaying,
			// so we can replace it with the previous node
			alreadyRenderedNodeToItsReplacementInNewGraph.set(node, previousNodeInFilteredGraph);
		} else if (previousNodeInFilteredGraph != null &&
			(previousNodeInFilteredGraph.causeType === 'Page.frameRequestedNavigation' || previousNodeInFilteredGraph.causeType === 'Page.navigatedWithinDocument') &&
			(node.causeType === 'Page.frameRequestedNavigation' || node.causeType === 'Page.navigatedWithinDocument') &&
			node.frameId === previousNodeInFilteredGraph.frameId &&
			node.url === previousNodeInFilteredGraph.url &&
			node.willCause.length > 0
		) {
			// This node provides no more information than the previous node, so we can replace it with the previous node
			alreadyRenderedNodeToItsReplacementInNewGraph.set(node, previousNodeInFilteredGraph);
		} else {
			// Render it
			const replacementNode: CausationNode = {
				...node, willCause: [],
				causedBy: node.causedBy.map(({howInferred, node: originalNode}) => 
						({howInferred, node: alreadyRenderedNodeToItsReplacementInNewGraph.get(originalNode)})
					).filter( n => n.node != null) as CausationEdge[],
			};
			for (const {howInferred, node: causedByNode} of replacementNode.causedBy) {
				causedByNode.willCause.push({howInferred, node: replacementNode});
			}
			alreadyRenderedNodeToItsReplacementInNewGraph.set(node, replacementNode);
			filteredOrderedNodes.push(replacementNode);
			previousNodeInFilteredGraph = replacementNode;
		}
		// Determine if any of the nodes that this node causes have now had all their
		// causes rendered, in which case they become ready to render.
		const newlyReadyNodesWithAllCausesRendered = node.willCause.filter(({node: willCauseNode}) =>
			willCauseNode.causedBy.every(({node}) =>
				alreadyRenderedNodeToItsReplacementInNewGraph.has(node)
			)
		);
		// Add any new nodes that are ready to render to the ready node list
		// in causal (time-consistent) order.
		for (const {node: newlyReadyNode} of newlyReadyNodesWithAllCausesRendered) {
			const index = readyNodes.findIndex(n => compareCausationNodes(newlyReadyNode, n) < 0);
			if (index === -1) {
				readyNodes.push(newlyReadyNode);
			} else {
				readyNodes.splice(index, 0, newlyReadyNode);
			}
		}
	}
	return filteredOrderedNodes;
};
